import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useAnnouncementAuth,
  useAnnouncementService,
} from "../service";
import type {
  AnnouncementComment,
  AnnouncementCommentEdit,
  CommentTreeNode,
} from "../types";
import {
  buildCommentTree,
  buildEmployeeDisplayName,
  isChartmetricEmail,
} from "../utils";

interface ReplyContext {
  parentCommentId: number;
  parentUserName: string | null;
}

interface Params {
  announcementId: number;
  initialComments?: AnnouncementComment[];
  isExpanded: boolean;
}

export default function useComments({
  announcementId,
  initialComments,
  isExpanded,
}: Params) {
  const service = useAnnouncementService();
  const auth = useAnnouncementAuth();
  const [comments, setComments] = useState<AnnouncementComment[]>(
    initialComments ?? [],
  );
  const [newComment, setNewComment] = useState("");
  const [editedDisplayName, setEditedDisplayName] = useState<string | null>(
    null,
  );
  const [replyingTo, setReplyingTo] = useState<ReplyContext | null>(null);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const canSetDisplayName = auth.isAuthenticated && isChartmetricEmail(auth.email);
  const displayName = editedDisplayName ?? buildEmployeeDisplayName(auth.firstName);

  const refresh = useCallback(async (): Promise<void> => {
    const fetched = await service.fetchComments(announcementId);
    setComments(fetched);
    setHasLoaded(true);
  }, [announcementId, service]);

  useEffect(() => {
    if (!isExpanded || hasLoaded) return;
    const controller = new AbortController();
    void service
      .fetchComments(announcementId, controller.signal)
      .then((fetched) => {
        setComments(fetched);
        setHasLoaded(true);
      })
      .catch(() => {
        /* swallow */
      });
    return () => controller.abort();
  }, [announcementId, hasLoaded, isExpanded, service]);

  const commentTree = useMemo<CommentTreeNode[]>(
    () => buildCommentTree(comments),
    [comments],
  );

  const commentCount = useMemo(
    () => comments.filter((c) => c.deleted_at === null).length,
    [comments],
  );

  const handleAddComment = useCallback(async (): Promise<void> => {
    if (!auth.isAuthenticated) {
      auth.requestSignIn();
      return;
    }
    const trimmed = newComment.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const trimmedDisplayName = displayName.trim();
      await service.createComment(announcementId, {
        content: trimmed,
        parent_comment_id: replyingTo?.parentCommentId ?? null,
        display_name:
          canSetDisplayName && trimmedDisplayName ? trimmedDisplayName : undefined,
        images: pendingImages,
      });
      setNewComment("");
      setPendingImages([]);
      setReplyingTo(null);
      await refresh();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    announcementId,
    auth,
    canSetDisplayName,
    displayName,
    isSubmitting,
    newComment,
    pendingImages,
    refresh,
    replyingTo,
    service,
  ]);

  const handleEditComment = useCallback(
    async (commentId: number, edit: AnnouncementCommentEdit): Promise<boolean> => {
      const trimmed = edit.content.trim();
      if (!trimmed || editingCommentId !== null) return false;
      setEditingCommentId(commentId);
      try {
        const updatedComment = await service.editComment(announcementId, commentId, {
          content: trimmed,
          display_name:
            canSetDisplayName && edit.displayName !== undefined
              ? edit.displayName.trim()
              : undefined,
          remove_attachment_ids: edit.removeAttachmentIds,
          images: edit.newImages,
        });
        setComments((prev) =>
          prev.map((comment) => (comment.id === commentId ? updatedComment : comment)),
        );
        return true;
      } catch (error) {
        console.error("Failed to edit comment:", error);
        return false;
      } finally {
        setEditingCommentId(null);
      }
    },
    [announcementId, canSetDisplayName, editingCommentId, service],
  );

  const handleDeleteComment = useCallback(
    async (commentId: number): Promise<void> => {
      await service.deleteComment(announcementId, commentId);
      await refresh();
    },
    [announcementId, refresh, service],
  );

  const handleReply = useCallback(
    (commentId: number, userName: string | null): void => {
      if (!auth.isAuthenticated) {
        auth.requestSignIn();
        return;
      }
      setReplyingTo({ parentCommentId: commentId, parentUserName: userName });
    },
    [auth],
  );

  const cancelReply = useCallback((): void => {
    setReplyingTo(null);
  }, []);

  return {
    commentTree,
    commentCount,
    newComment,
    setNewComment,
    canSetDisplayName,
    displayName,
    setDisplayName: setEditedDisplayName,
    pendingImages,
    setPendingImages,
    replyingTo,
    cancelReply,
    handleAddComment,
    handleEditComment,
    handleDeleteComment,
    handleReply,
    isSubmitting,
    editingCommentId,
    isAuthenticated: auth.isAuthenticated,
    requestSignIn: auth.requestSignIn,
    currentUserId: auth.currentUserId,
  };
}
